import type { KanhaChatConfig } from "./types";
import {
  DEFAULT_EMBEDDING_MODEL,
  DEFAULT_RERANKER_MODEL,
  type RerankerProvider,
  type RetrievalProgress,
  type SemanticEmbeddingProvider,
} from "./hybrid-retrieval";

declare const __KANHA_RETRIEVAL_WORKER_URL__: string;

type Pending = {
  resolve(values: number[][] | number[]): void;
  reject(error: unknown): void;
  onProgress?: (progress: RetrievalProgress) => void;
};

type ReadyEntry = {
  ready: boolean;
  error: unknown | null;
  pending: Promise<void>;
};

let worker: Worker | null = null;
let workerBootstrapUrl: string | null = null;
let workerModuleUrl: string | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();

export function resolveWorkerModuleUrl(value: string | URL | undefined, fallback: string, name: string) {
  if (value instanceof URL) return value.href;
  const workerUrl = value || fallback;
  if (/^[a-z][a-z0-9+.-]*:/i.test(workerUrl)) return workerUrl;
  if (typeof document === "undefined") {
    throw new Error(`Relative ${name} requires a browser document`);
  }
  return new URL(workerUrl, document.baseURI).href;
}

export function resolvedRetrievalWorkerUrl(value?: string | URL) {
  return resolveWorkerModuleUrl(value, __KANHA_RETRIEVAL_WORKER_URL__, "retrievalWorkerUrl");
}

function workerInstance(retrievalWorkerUrl?: string | URL) {
  if (typeof Worker === "undefined") throw new Error("Retrieval Web Worker is unavailable");
  const moduleUrl = resolvedRetrievalWorkerUrl(retrievalWorkerUrl);
  if (worker && workerModuleUrl === moduleUrl) return worker;
  if (worker && pending.size > 0) {
    throw new Error("Cannot change retrievalWorkerUrl while retrieval is running");
  }
  if (worker) {
    clearWorkerReadiness();
  }
  workerModuleUrl = moduleUrl;
  workerBootstrapUrl = URL.createObjectURL(new Blob(
    [`import ${JSON.stringify(moduleUrl)};`],
    { type: "text/javascript" },
  ));
  worker = new Worker(workerBootstrapUrl, { type: "module" });
  worker.onmessage = (event: MessageEvent<{
    type: "result" | "error" | "progress";
    id: number;
    values?: number[][] | number[];
    message?: string;
    stage?: RetrievalProgress["stage"];
    progress?: number;
    text?: string;
  }>) => {
    if (workerBootstrapUrl) {
      URL.revokeObjectURL(workerBootstrapUrl);
      workerBootstrapUrl = null;
    }
    const request = pending.get(event.data.id);
    if (!request) return;
    if (event.data.type === "progress") {
      request.onProgress?.({
        stage: event.data.stage || "embedding",
        progress: event.data.progress || 0,
        text: event.data.text || "Retrieving context",
      });
      return;
    }
    pending.delete(event.data.id);
    if (event.data.type === "error") {
      request.reject(new Error(event.data.message || "Retrieval worker failed"));
    } else {
      request.resolve(event.data.values || []);
    }
  };
  worker.onerror = (event) => {
    if (workerBootstrapUrl) {
      URL.revokeObjectURL(workerBootstrapUrl);
      workerBootstrapUrl = null;
    }
    for (const request of pending.values()) {
      request.reject(new Error(event.message || "Retrieval worker failed to load"));
    }
    pending.clear();
    clearWorkerReadiness();
  };
  return worker;
}

function run(
  message: Record<string, unknown>,
  isCancelled: (() => boolean) | null,
  onProgress?: (progress: RetrievalProgress) => void,
  retrievalWorkerUrl?: string | URL,
) {
  const id = nextId;
  nextId += 1;
  return new Promise<number[][] | number[]>((resolve, reject) => {
    const target = workerInstance(retrievalWorkerUrl);
    pending.set(id, { resolve, reject, onProgress });
    target.postMessage({ ...message, id });
    const timer = isCancelled && setInterval(() => {
      if (!isCancelled()) return;
      if (timer) clearInterval(timer);
      if (!pending.delete(id)) return;
      target.postMessage({ type: "cancel", id });
      reject(new DOMException("Retrieval cancelled", "AbortError"));
    }, 50);
    const request = pending.get(id)!;
    pending.set(id, {
      ...request,
      resolve: (values) => {
        if (timer) clearInterval(timer);
        resolve(values);
      },
      reject: (error) => {
        if (timer) clearInterval(timer);
        reject(error);
      },
    });
  });
}

export const SEMANTIC_READY_WAIT_MS = 1500;
export const RERANKER_READY_WAIT_MS = 1500;

const MAX_CACHED_CORPORA = 2;
const semanticIndexes = new Map<string, ReadyEntry>();
const rerankerLoads = new Map<string, ReadyEntry>();

function clearWorkerReadiness() {
  worker?.terminate();
  worker = null;
  workerModuleUrl = null;
  semanticIndexes.clear();
  rerankerLoads.clear();
}

function waitUpTo(
  entry: ReadyEntry,
  milliseconds: number,
  message: string,
  isCancelled: () => boolean,
) {
  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      clearInterval(readinessTimer);
      if (error) reject(error);
      else resolve();
    };
    const check = () => {
      if (isCancelled()) {
        finish(new DOMException("Retrieval cancelled", "AbortError"));
      } else if (entry.error) {
        finish(entry.error);
      } else if (entry.ready) {
        finish();
      }
    };
    const timeout = setTimeout(() => {
      check();
      if (!settled) finish(new Error(message));
    }, milliseconds);
    const readinessTimer = setInterval(check, 50);
    check();
  });
}

export function createBrowserSemanticProvider(
  model = DEFAULT_EMBEDDING_MODEL,
  retrievalWorkerUrl?: string | URL,
  readyWaitMs = SEMANTIC_READY_WAIT_MS,
): SemanticEmbeddingProvider {
  const indexFor = (
    passages: string[],
    cacheKey: string,
    onProgress?: (progress: RetrievalProgress) => void,
    precomputedVectors?: number[][],
  ) => {
    const moduleUrl = resolvedRetrievalWorkerUrl(retrievalWorkerUrl);
    const key = `${moduleUrl}:${model}:${cacheKey}:${passages.length}:${precomputedVectors ? "precomputed" : "local"}`;
    let entry = semanticIndexes.get(key);
    if (!entry) {
      const pending = run(
        { type: "index", model, cacheKey, passages, precomputedVectors },
        null,
        onProgress,
        moduleUrl,
      ).then(() => {
        created.ready = true;
      });
      const created: ReadyEntry = { ready: false, error: null, pending };
      entry = created;
      while (semanticIndexes.size >= MAX_CACHED_CORPORA) {
        semanticIndexes.delete(semanticIndexes.keys().next().value!);
      }
      semanticIndexes.set(key, entry);
      pending.catch((error) => {
        created.error = error;
        semanticIndexes.delete(key);
      });
    }
    return { entry, moduleUrl };
  };
  return {
    model,
    async prepare(passages, { cacheKey, onProgress, precomputedVectors }) {
      await indexFor(passages, cacheKey, onProgress, precomputedVectors).entry.pending;
    },
    async embed(texts, { cacheKey, isCancelled, onProgress }) {
      const passages = texts.slice(1);
      const precomputedKey = `${resolvedRetrievalWorkerUrl(retrievalWorkerUrl)}:${model}:${cacheKey}:${passages.length}:precomputed`;
      const precomputedEntry = semanticIndexes.get(precomputedKey);
      const { entry, moduleUrl } = precomputedEntry
        ? { entry: precomputedEntry, moduleUrl: resolvedRetrievalWorkerUrl(retrievalWorkerUrl) }
        : indexFor(passages, cacheKey, onProgress);
      if (!entry.ready) {
        await waitUpTo(
          entry,
          readyWaitMs,
          "Semantic index is still being prepared",
          isCancelled,
        );
      }
      return run({
        type: "embed",
        model,
        cacheKey,
        query: texts[0] || "",
        passages,
      }, isCancelled, onProgress, moduleUrl) as Promise<number[][]>;
    },
  };
}

export function createBrowserRerankerProvider(
  model = DEFAULT_RERANKER_MODEL,
  retrievalWorkerUrl?: string | URL,
  readyWaitMs = RERANKER_READY_WAIT_MS,
): RerankerProvider {
  const load = (onProgress?: (progress: RetrievalProgress) => void) => {
    const moduleUrl = resolvedRetrievalWorkerUrl(retrievalWorkerUrl);
    const key = `${moduleUrl}:${model}`;
    let entry = rerankerLoads.get(key);
    if (!entry) {
      const pending = run(
        { type: "load-reranker", model },
        null,
        onProgress,
        moduleUrl,
      ).then(() => {
        created.ready = true;
      });
      const created: ReadyEntry = { ready: false, error: null, pending };
      entry = created;
      rerankerLoads.set(key, entry);
      pending.catch((error) => {
        created.error = error;
        rerankerLoads.delete(key);
      });
    }
    return { entry, moduleUrl };
  };
  return {
    model,
    async prepare(options) {
      await load(options?.onProgress).entry.pending;
    },
    async score(query, passages, { isCancelled, onProgress }) {
      const { entry, moduleUrl } = load(onProgress);
      if (!entry.ready) {
        await waitUpTo(
          entry,
          readyWaitMs,
          "Passage reranker is still loading",
          isCancelled,
        );
      }
      return run(
        { type: "rerank", model, query, passages },
        isCancelled,
        onProgress,
        moduleUrl,
      ) as Promise<number[]>;
    },
  };
}

export function groundedRetrievalProviders(
  config: Pick<
    KanhaChatConfig,
    "ragRetrievalMode" | "ragEmbeddingModel" | "ragRerankerModel" | "retrievalWorkerUrl"
  >,
  readyWaitMs?: number,
) {
  return {
    semantic: config.ragRetrievalMode === "bm25"
      ? undefined
      : createBrowserSemanticProvider(
        config.ragEmbeddingModel,
        config.retrievalWorkerUrl,
        readyWaitMs,
      ),
    reranker: config.ragRerankerModel
      ? createBrowserRerankerProvider(
        config.ragRerankerModel,
        config.retrievalWorkerUrl,
        readyWaitMs,
      )
      : undefined,
  };
}
